/**
 * OKS-16 / OKS-17 / OKS-18 — Inertia prop shapes for the candidate
 * recruiting surfaces (list + detail + manual-add + bulk-CSV import).
 *
 * Locked at TRD publish + reconciled with Backend TRDs §API Surface.
 * Every snake_case field mirrors the Rails Inertia serialiser 1:1
 * (see `Candidates::ImportResult` value object + `CandidatesController`
 * strong-params); the FE never re-shapes on the wire.
 *
 * Wire-format conventions:
 *   - Most prop keys are snake_case (Rails default).
 *   - The bulk-CSV `maxRows` prop is camelCase per OKS-16's
 *     established serialiser (`import_page_props` in
 *     `candidate_imports_controller.rb`). Any drift is caught by Phase 4
 *     visual smoke + Vitest assertions.
 */

import type {
  FunnelData,
  InviteActions,
  InviteSummary,
  StageAnalyticsSection,
} from "./invites"

import type { CurrentBrand, CurrentUser } from "./index"

// ─── Status enum ───────────────────────────────────────────────────

/**
 * 9-value status pipeline for candidates. Mirrors `Candidate::STATUSES`
 * server-side. OKS-16 only sets `"new"`; OKS-19 owns the transition graph;
 * OKS-18 reads + displays; OKS-17 filters across every value.
 *
 * OKS-948 — added `pending_brand_validation` and `contacted`. Note that
 * `approved` is RELABELLED to "To contact" at the i18n layer only: the enum
 * value is unchanged and must not be renamed.
 *
 * Order is the canonical pipeline order — the tab strip, the filter rail and
 * the multi-select chips all follow this sequence.
 *
 * ⚠️ This is a HAND-WRITTEN mirror of `Candidate::STATUSES`; nothing derives
 * one from the other. Reorder here without reordering there and the tab strip
 * silently disagrees with the server's facet order, with nothing red.
 * `types/__tests__/candidate-status-order.test.ts` pins both against the UX
 * Spec §2 sequence — keep it that way.
 */
export type CandidateStatus =
  | "new"
  | "reviewed"
  | "pending_brand_validation"
  | "approved"
  | "contacted"
  | "invited"
  | "joined"
  | "rejected"
  | "blocklisted"

export const CANDIDATE_STATUSES: readonly CandidateStatus[] = [
  "new",
  "reviewed",
  "pending_brand_validation",
  "approved",
  "contacted",
  "invited",
  "joined",
  "rejected",
  "blocklisted",
] as const

// ─── OKS-16 — bulk-import shapes (unchanged) ───────────────────────

/**
 * Recognised per-row failure codes from `Candidates::ImportService`.
 *
 * Pinned to a string-literal union so the consumer (`ImportResultPanel`)
 * can exhaustively switch on a `reason_code` and pull the localised
 * copy from the per-code i18n key. Backend ships
 * `reason_message` as a fallback for the `unknown` discriminant — UI
 * still prefers the i18n key when the code is recognised.
 */
export type ImportFailureCode =
  | "missing_required"
  | "malformed_handle"
  | "followers_not_numeric"
  | "duplicate_in_file"
  | "duplicate_in_brand"
  | "row_too_long"
  | "unknown"

export interface ImportFailure {
  row: number
  tiktok_handle: string | null
  reason_code: ImportFailureCode
  reason_message: string
  field?: string | null
  first_row?: number | null
}

export interface ImportResult {
  created_count: number
  skipped_count: number
  total_rows: number
  failures: ImportFailure[]
}

// ─── OKS-17 — Candidate List ───────────────────────────────────────

/**
 * Single row in the candidate list — wire shape for OKS-17 + OKS-348.
 *
 * `status` is the full enum (not hardcoded `"new"`). `location` is a
 * structured `{ city, country }` object where `country` is an ISO
 * alpha-2 code (post-OKS-348) and `city` is optional freetext (≤120
 * chars; blank/null when unknown, per OKS-713).
 */
export interface CandidateRow {
  id: number
  tiktok_handle: string
  display_name: string
  approx_followers: number | null
  status: CandidateStatus
  location: {
    /** OKS-713 — Optional freetext city (≤120 chars); blank/null when unknown. */
    city: string | null
    /** ISO-3166-1 alpha-2 (`"FR"`, `"US"`, ...). FE renders the
     *  localized name via `lib/countries.ts#countryLabel`. */
    country: string | null
  }
  /**
   * Tag list — backend serialiser returns the brand-scoped tag set as
   * `string[]` for the wire (via the model's `tag_list` accessor,
   * filtered for non-empty values).
   */
  tags: string[]
  created_at: string
  /**
   * **OKS-952 additive** — the coded reason behind this row's rejection,
   * rendered as a badge beside the status.
   *
   * `null` for every non-rejected row, for rejections made before the taxonomy
   * shipped, and for rejections made without a code (it is optional). Those
   * rows render exactly as they did — no "unknown reason" placeholder, because
   * the reason was never asked for.
   *
   * A raw enum value; the label resolves via `t(\`candidates.reason_code.${code}\`)`.
   */
  rejection_reason_code?: string | null
  /**
   * **OKS-23 additive — TikTok link health state for non-active surfaces.**
   *
   * BE ships `null` when the link is `active` (FE renders no cue,
   * keeps the roster visually quiet per UX Spec §1.E). FE consumes
   * via `<TiktokLinkStateCue>` next to the handle column.
   */
  tiktok_link_state?: "refresh_needed" | "revoked" | null
  /**
   * **OKS-465 additive — Removed-view per-row Restore affordance.**
   *
   * Present ONLY in the Removed view (`?removed=1`). `string` = the
   * restore endpoint (`restoreCandidatePath(id)`), `null` = the viewer
   * may not restore (URL-nullability visibility rule — render DOM-absent).
   * Absent entirely in the active view.
   */
  restore_url?: string | null
}

/**
 * URL-driven filter state echoed back by the controller. Each
 * field corresponds to a `?param` on `/candidates`. The BE silently
 * drops off-whitelist values so URLs stay forgiving; the echo
 * always reflects the normalized server view of the request.
 *
 * **Default-France behavior (CTPO V1 lock):** server-side, when
 * `params[:country]` is *absent* AND the brand has any
 * `country = "France"` candidates, BE 303-redirects bare
 * `/candidates` to `/candidates?country=France`. To opt out (i.e.
 * "All countries"), the FE writes `?country=` (empty string preserved
 * in URL). The empty-string discriminates "user cleared" from "fresh
 * visit (apply default)". `useCandidateListUrl` honors this contract.
 */
export interface CandidateFilters {
  q: string | null
  status: CandidateStatus[]
  followers_min: number | null
  followers_max: number | null
  /**
   * Exact-match country filter. Non-null values map to BE's
   * `where(country: ...)` index lookup; empty string (`""`) means
   * "user explicitly cleared — do not apply default-France".
   */
  country: string | null
  /** ILIKE-contains match on the structured `city` column. */
  city: string | null
  tags: string[]
  /**
   * **OKS-953 (PRD R5)** — the candidate's CURRENT rejection reason: the
   * `reason_code` of the latest `reject` event, and only while
   * `status === "rejected"`. A candidate rejected, reverted and re-approved is
   * NOT a rejection and must not match — the server gates on status, mirroring
   * `Candidate#rejection_reason_code`.
   */
  reason_code: string[]
  /**
   * **OKS-953 (PRD R5)** — at least one logged attempt on any selected channel,
   * at ANY status. History, not current state: a creator we DM'd and then
   * rejected was still DM'd.
   */
  contact_channel: string[]
}

export type CandidateSortField = "created_at" | "approx_followers"
export type CandidateSortDir = "asc" | "desc"

export interface CandidateSort {
  field: CandidateSortField
  dir: CandidateSortDir
}

export interface CandidatePagination {
  page: number
  pages: number
  count: number
  prev: string | null
  next: string | null
}

/**
 * Server-authoritative facet counts for status (over the brand-scoped
 * relation, ignoring the active status filter — standard facet UX).
 * Tags + countries are plain DISTINCT lists at V1 scale.
 */
export interface CandidateFilterOptions {
  statuses: { value: CandidateStatus; count: number }[]
  tags: string[]
  countries: string[]
  /**
   * **OKS-953** — option universes for the two new filters. UNCOUNTED, unlike
   * `statuses`: counting would cost a second `GROUP BY` per request for
   * pickers nobody compares against, and `statuses` earns its counts because
   * the tab strip renders every one of them.
   */
  reason_codes: string[]
  contact_channels: string[]
}

/**
 * Props for `pages/Brand/Candidates/Index.tsx` (OKS-17).
 *
 * `is_empty` discriminates the empty-state CTA (`true`: brand has zero
 * candidates → CTA to add) from the no-results state (`false` +
 * `candidates.length === 0` → clear-filters affordance).
 */
export interface CandidatesIndexProps {
  candidates: CandidateRow[]
  filters: CandidateFilters
  sort: CandidateSort
  pagination: CandidatePagination
  filter_options: CandidateFilterOptions
  is_empty: boolean
  /**
   * **OKS-19 additive** — bulk-transition endpoint surface. The
   * `transition_url` is BE-supplied (via js-routes
   * `bulkCandidateTransitionsPath()`); FE never reconstructs. The
   * sticky-bottom bulk action bar posts to this URL with the
   * locked nested-strong-params wire format (`bulk_transition[event]`
   * etc.). See `CandidateBulkEndpoint`.
   */
  bulk: CandidateBulkEndpoint
  /**
   * **OKS-21 additive** — recruitment funnel data. Inertia 2.x
   * `<Deferred>`-shaped: `null` on first paint, hydrates on the
   * server-side roundtrip. Date-range filtering uses
   * `router.reload({ only: ["funnel"], data: { from, to } })` —
   * same-page partial reload, no separate route.
   */
  funnel: FunnelData | null
  /**
   * **OKS-956 additive** — time-in-stage p50/p90, rendered under the funnel
   * widget. A SEPARATE deferred key from `funnel`: the range filter reloads
   * `only: ["funnel"]`, and this figure has no range to scope.
   *
   * Optional AND nullable, and the two mean different things: **absent** on
   * first paint (deferred, not yet resolved — `<Deferred>` shows the
   * skeleton), **`null`** once resolved for a viewer the funnel gate refuses
   * (render nothing at all — not the error state).
   */
  stage_analytics?: StageAnalyticsSection | null
  /**
   * **OKS-127 additive** — recency-highlight marker for the just-added
   * row. BE `#create` redirects with `?just_added=<id>`; the index
   * controller coerces the param via `.presence&.to_i` and surfaces it
   * here. `null` (no marker / non-numeric / blank) ⇒ no highlight
   * renders. Matching row id receives a one-shot `bg-accent/30`→
   * transparent 1500ms ease-out fade; the URL marker is scrubbed via
   * `history.replaceState` on mount so back-button / refresh do not
   * re-trigger. Tampered foreign ids silently no-op (no row in the
   * brand-scoped list matches).
   */
  just_added_id: number | null
  /**
   * **OKS-465 additive** — the FE is rendering the Removed view
   * (`?removed=1`). Drives the Active⇄Removed toggle state + the
   * Removed-view empty/no-results copy.
   */
  removed_view: boolean
  /**
   * **OKS-465 additive** — count of soft-removed candidates in the
   * brand. Drives the Active/Removed toggle label and the "View
   * removed (N)" affordance on the all-removed active empty state.
   */
  removed_count: number
  /**
   * **OKS-465 additive** — the CURRENT view's base scope (active OR
   * removed), IGNORING filters, is empty. Lets the FE distinguish "no
   * active candidates at all (all removed)" → "View removed (N)" from
   * "filters hid everything" → clear-filters, without conflating either
   * with the brand-new `is_empty` add-first CTA.
   */
  view_is_empty: boolean
  /**
   * **OKS-629 additive** — add-candidate Sheet open state, derived from
   * the `?add=1` URL param (`params[:add].present?`). Drives the
   * `AddCandidateSheet`'s `open` prop so the panel survives refresh +
   * share and closes on the server redirect after a successful create.
   * The `openSheet`/`closeSheet` helpers partial-reload only this key.
   */
  show_add: boolean
}

// ─── OKS-18 — Candidate Detail ─────────────────────────────────────

/**
 * Full candidate profile shipped on the detail page. Wider than
 * `CandidateRow` — adds `bio`, `notes` + last-edit stamp,
 * `created_by` user reference. Status timeline + cross-brand signals
 * + actions are top-level props alongside this.
 */
export interface CandidateDetail {
  id: number
  tiktok_handle: string
  display_name: string
  bio: string | null
  approx_followers: number | null
  /**
   * **OKS-601 — Optional recruitment-stage contact email.** `null` when
   * unset (blank submits persist as SQL `NULL`, never `""`). Brand-owned
   * intelligence — distinct from the OKS-466 account email. Editable
   * inline on the profile via `CandidateEmailField`.
   */
  email: string | null
  /** OKS-348 — Plain `{ city, country }`; country is ISO alpha-2. */
  location: {
    city: string | null
    country: string | null
  }
  tags: string[]
  status: CandidateStatus
  notes: string | null
  notes_updated_by: { id: number; name: string } | null
  notes_updated_at: string | null
  created_at: string
  created_by: { id: number; name: string }
  /**
   * **OKS-23 additive — TikTok OAuth verification state.**
   *
   * BE ships `true` when the candidate has an active TikTok
   * SocialAccount linked (via OKS-22 atomic-create or OKS-23 relink).
   * FE renders `<TiktokVerifiedBadge>` next to the handle on the
   * Candidate detail breadcrumb when this is true.
   */
  tiktok_verified?: boolean
  /**
   * **OKS-23 additive — TikTok link health state for non-active surfaces.**
   *
   * BE ships:
   *   - `null` when active (FE renders no cue — keeps roster quiet
   *     per UX Spec §1.E).
   *   - `"refresh_needed"` when the token's expired but refreshable.
   *   - `"revoked"` when the creator has revoked TikTok-side or
   *     repeated refresh failures have flipped the link.
   */
  tiktok_link_state?: "refresh_needed" | "revoked" | null
}

/**
 * Status-history event row. BE adapter returns ≥1 row always:
 *   - if OKS-19's `candidate_status_events` table exists → real rows;
 *   - else → single synthetic creation event with `from: null` +
 *     `to: candidate.status` + `actor: candidate.created_by`.
 *
 * **OKS-19 additive:** `reason` is non-null on `reject` events
 * (when the actor supplied one) and `blocklist` events (always — DB
 * CHECK + model validation enforce REQUIRED). Other events ignore
 * the field. `StatusHistoryTimeline` renders `reason` below the actor
 * line ONLY when non-null. Cross-brand exposure is blocked at BE —
 * timeline endpoint scopes per-candidate, so reason text never crosses
 * the brand boundary (P0 leak floor 90 — covered by QA E11 marquee).
 *
 * The OKS-18 4-key contract (`{from, to, actor, at}`) is preserved;
 * `reason` is additive and ignored by the OKS-18 `StatusHistory`
 * adapter when absent (BE TRD Port Assessment §Adapter).
 */
export interface CandidateStatusEvent {
  from: CandidateStatus | null
  to: CandidateStatus
  /**
   * **OKS-465 additive** — the event name (e.g. `"approve"`, `"reject"`,
   * `"remove"`, `"restore"`). The timeline renders `remove` / `restore`
   * lifecycle rows distinctly ("Removed / Restored by X" — those rows
   * carry `from === to`); transition rows keep their from→to rendering.
   * `null` only on the pre-OKS-19 synthetic creation row.
   */
  event: string | null
  actor: { id: number; name: string } | null
  at: string
  reason: string | null
}

/**
 * **OKS-465** — Removal metadata for the removed-state detail banner.
 * `null` while the candidate is active; populated while removed.
 */
export interface CandidateRemoval {
  /** ISO-8601 timestamp the candidate was removed. */
  at: string
  /** Actor who removed it (`null` if that user row was since deleted). */
  by: { id: number; name: string } | null
}

/**
 * Cross-brand intelligence aggregate signals. **Aggregate-only —
 * never carries brand identifiers (P0 leak guard, BE TRD locked).**
 * V1 stub returns `{ overlap_count: 0, previous_rejections_count: 0,
 * blocklist_flag: false }`; OKS-20 swaps the BE service internals
 * with no FE / QA-test churn.
 *
 * `null` on first paint per Inertia deferred-prop contract — FE
 * renders skeleton until hydrate.
 */
export interface CrossBrandSignals {
  overlap_count: number
  previous_rejections_count: number
  blocklist_flag: boolean
}

/**
 * **OKS-628** — TikTok profile oEmbed payload for the candidate detail
 * page. Rides a **deferred** Inertia prop (`tiktok_profile_embed`) so it
 * never blocks first paint (mirrors `cross_brand_signals`).
 *
 * `html` is a server-**sanitized** `<blockquote class="tiktok-embed"
 * data-embed-type="creator">` that TikTok's `embed.js` hydrates into the
 * profile card client-side. `null` for EVERY non-success state (missing /
 * invalid handle, private/deleted account, oEmbed error/timeout) — the FE
 * renders one graceful fallback block for all of them.
 *
 * The prop itself is `null` on first paint (deferred contract); the
 * resolved value is `{ html: string | null }`.
 */
export interface TiktokProfileEmbed {
  html: string | null
}

/**
 * Per-candidate transition affordance URLs. **OKS-19 8-key shape**,
 * reduced to 6 by OKS-566 (`mark_joined_url`) and OKS-674 (`invite_url`)
 * (locked bilateral with be-oks19 + ux-oks19; FE TRD §State & Data
 * Flow). Each field is `string | null`; `null` is the **single
 * visibility discriminator** — a null URL means the affordance is
 * either state-illegal (e.g. `approve_url` when `status != "reviewed"`)
 * OR role-locked (e.g. `blocklist_url` for non-OKS-Admin). FE renders
 * **DOM-absent** in both cases — NO disabled-with-tooltip path.
 *
 * The OKS-18 disabled-tooltip placeholder (`Approval workflow ships
 * in OKS-19`) was a temporal stand-in while OKS-19 was unshipped;
 * once OKS-19 lands the disabled-tooltip semantics are dead code and
 * removed in the same PR (FE TRD F7 + grep-sweep in F12).
 *
 * All 8 events route to the same locked URL contract:
 *   POST /candidates/:id/transitions
 *   body: { transition: { event: <event>, reason?: string } }
 * The `reason` body parameter is REQUIRED for `blocklist`, OPTIONAL
 * for `reject`/`unblocklist`, and IGNORED for the others.
 *
 * For `event: "revert"`, BE service decodes the source state at call
 * time — the same wire string covers both `approved → reviewed` and
 * `rejected → reviewed` (BE TRD §Key Decisions).
 */
export interface CandidateActions {
  /** Non-null when current status is `new` (forward step). */
  mark_reviewed_url: string | null
  /** Non-null when current status is `reviewed` (forward step). */
  approve_url: string | null
  /**
   * OKS-948 — non-null when current status is `reviewed`. Hands the
   * candidate to the brand for a yes/no. Optional step: the direct
   * Reviewed → To contact path via `approve_url` still works.
   */
  request_brand_validation_url: string | null
  /**
   * OKS-948 — non-null when current status is `pending_brand_validation`
   * AND the viewer is a BRAND member (or an OKS Admin acting as the brand).
   * An agency member never receives this URL — an agency cannot bless its
   * own candidate (PRD AC3). Gate is `CandidatePolicy#brand_validation?`.
   */
  validate_url: string | null
  /*
   * OKS-951 — `mark_contacted_url` was REMOVED from this contract.
   *
   * The server no longer emits the key (`mark_contacted` left
   * `CandidatesController::BAR_TRANSITION_EVENTS`) and `#transitions` refuses
   * the event outright. A bare status flip produced a `contacted` candidate
   * with no evidence anyone contacted them — who then sat in « À relancer »
   * showing zero attempts and was auto-rejected at +17 with `no_response`.
   *
   * Contacting goes through `POST /candidates/:id/contact_attempts`
   * (`contactAttemptsCandidatePath`), which writes the attempt row and the
   * status change in one transaction.
   */
  /** Non-null when current status is `reviewed` (sibling of approve). */
  reject_url: string | null
  /**
   * Non-null when current status is `approved`, `contacted` OR `rejected`.
   * OKS-948 — the DESTINATION now varies; read it from the sibling
   * `revert_destination` prop, never re-derive it from the status here.
   */
  revert_url: string | null
  // OKS-674 — `invite_url` removed. The redundant action-bar Invite CTA
  // was deleted; generating an invite link in the Invitations section
  // (`CandidateInvites::GenerateService`) is the single affordance that
  // drives `approved → invited`. The `invite` state-machine event itself
  // is retained BE-side; it's just no longer surfaced as a bar URL.
  // OKS-566 — `mark_joined_url` removed. `joined` has no inbound
  // transition action; a candidate self-joins via the invite link
  // (`CandidateInvites::CompleteService`).
  /** Non-null only for OKS-Admin AND current status != `blocklisted`. */
  blocklist_url: string | null
  /** Non-null only for OKS-Admin AND current status == `blocklisted`. */
  unblocklist_url: string | null
}

/**
 * **OKS-465** — Remove/restore affordance URLs for the detail page.
 * SEPARATE from `CandidateActions` (the OKS-19 transition surface),
 * because remove/restore are not status transitions — they're a
 * lifecycle concern. Mirrors the `invite_actions` pattern. Each is
 * `string | null`; `null` means the affordance is DOM-absent (state
 * guard or policy). At most one is non-null at a time.
 */
export interface CandidateRemovalActions {
  /**
   * Non-null when the candidate is ACTIVE and the viewer is a
   * brand-admin-tier user (or OKS admin acting-as). Drives the action
   * bar's destructive "Remove from pipeline" button (AlertDialog confirm).
   */
  remove_url: string | null
  /**
   * Non-null when the candidate is REMOVED and the viewer may restore.
   * Drives the removed-state detail banner's Restore button.
   */
  restore_url: string | null
}

/**
 * Bulk-action endpoint surface for the candidate list page (OKS-19).
 * Top-level prop on `CandidatesIndexProps`. The `transition_url`
 * value comes from the BE js-routes helper `bulkCandidateTransitionsPath()`
 * — FE never reconstructs.
 *
 * Wire payload (Inertia `<Form>` POST):
 *   { bulk_transition: { event: "approve"|"reject", candidate_ids: number[<=25], reason?: string } }
 *
 * BE responds with 303 → `/candidates` (preserves the user's pre-submit
 * filter URL via Referer) plus structured flash payload — see
 * `BulkResult` below.
 */
export interface CandidateBulkEndpoint {
  transition_url: string
  /**
   * **OKS-950** — the bulk verbs THIS viewer may use on THIS brand:
   * `Candidates::BulkTransition::SUPPORTED_EVENTS` ∩ actor-permitted ∩
   * setting-eligible.
   *
   * The URL-nullability rule, extended to the bulk bar. `validate` appears
   * only for brand members, so an agency member is never offered a button
   * that would skip all 25 rows; `approve` is absent entirely on a brand set
   * to `always`, which is the widest door to an un-validated approval.
   *
   * ⚠️ Render buttons from `events` intersected with what the active status
   * tab supports — never from the tab alone, and never re-derive permission
   * on the client.
   */
  events: BulkTransitionEvent[]
}

/** **OKS-950** — mirrors `Candidates::BulkTransition::SUPPORTED_EVENTS`. */
export type BulkTransitionEvent = "approve" | "reject" | "validate"

/** **OKS-950** — mirrors `Brand::CANDIDATE_VALIDATION_VALUES` (CTPO D4). */
export type CandidateValidationSetting = "always" | "on_request" | "never"

/** **OKS-950** — props for `Brand/Settings/Candidates`. */
export interface BrandSettingsCandidatesProps {
  brand: {
    candidate_validation: CandidateValidationSetting
    /**
     * **OKS-951** — days after the cycle start at which follow-up 1, 2, …
     * become due. Ascending, non-empty. Read through `Brand`'s accessors, which
     * coerce and range-check, so a hand-edited `config` renders as the default
     * rather than blanking the form.
     */
    follow_up_offset_days: number[]
    /** **OKS-951** — days after the cycle start at which auto-close fires. */
    auto_close_after_days: number
  }
  /**
   * **OKS-951** — the brand's single active pitch template plus its two
   * brand-level free-text fields (`{{crew_name}}` / `{{perks}}` have no backing
   * column anywhere — PRD OQ3).
   */
  pitch: {
    body: string
    crew_name: string
    perks: string
    /**
     * The closed set of legal merge fields, server-supplied from
     * `Candidates::PitchRenderer::MERGE_FIELDS`. Listed in the form so a user
     * discovers the legal fields by reading rather than by being refused — and
     * derived rather than hand-written here, since a second list is how the
     * help text and the validator drift apart.
     */
    merge_fields: string[]
  }
  /**
   * **OKS-951** — `follow_up_offset_days.length + 1`, rendered READ-ONLY.
   *
   * ⚠️ Never an input. A stored maximum that can disagree with the length of
   * the offsets list is a drift bug with no symptom (PRD R3-a), so the UI must
   * not offer a way to set one.
   */
  derived_max_attempts: number
  /**
   * Server-supplied option order, from `Brand::CANDIDATE_VALIDATION_VALUES`.
   * Iterated rather than hard-coded so the radio set cannot drift from the
   * values the model will actually accept.
   */
  options: CandidateValidationSetting[]
  /**
   * Whether an agency currently operates this brand. Drives ONE thing: OQ11's
   * neutral line, shown only alongside `never`.
   *
   * ⚠️ Copy only. The setting itself is never derived from agency presence —
   * that is what makes "removing the agency doesn't change the setting" true
   * by construction (PRD AC2).
   */
  has_agency: boolean
  /**
   * `BrandSettingsCandidatesPolicy#update?`. ⚠️ Unlike every sibling settings
   * policy this one DENIES agency members — an agency must not be able to
   * switch off the brand's oversight of the agency's own picks.
   */
  can_edit: boolean
}

/**
 * Bulk-transition result payload, nested inside `flash[:success]`
 * (NOT a top-level flash key — `flash_keys` allow-list stays
 * `[:notice, :alert, :success, :error]` per memory
 * `feedback_inertia_rails_flash_keys`). FE consumes via
 * `usePage().props` after a 303 redirect from the bulk endpoint.
 *
 * The `View skipped` link in the summary toast is FE-built from
 * `skipped_ids` — href = `candidatesPath({bulk_skipped: skipped_ids.join(",")})`.
 * Counts are emitted per BE-locked Option B contract.
 *
 * `skipped_ids` carries **only** integer ids (no `tiktok_handle`,
 * no `reason_code`, no `error_message`). Reason codes
 * (`not_found / forbidden / invalid_transition / race`) are
 * service-internal and logged via `Rails.logger.tagged("bulk_transition")`,
 * never exposed user-facing (BE TRD §Key Decisions).
 */
export interface BulkResult {
  transitioned_count: number
  skipped_count: number
  skipped_ids: number[]
  event: "approve" | "reject"
}

/**
 * Discriminated success-flash payload. The OKS-18-shipped pattern
 * uses `flash[:success]` as a plain string (`"Notes saved"` etc.);
 * OKS-19 extends the value to optionally carry a structured
 * `bulk_result` payload while preserving backward compatibility.
 *
 * Consumers:
 *   - `useFlash` (hooks/use-flash.tsx) — toasts `message` (or the bare
 *     string form) via sonner.success; explicitly skips
 *     `bulk_result`-only payloads (`Index.tsx` owns that toast UX
 *     because it carries an Inertia router-coupled `View skipped` link).
 *   - `pages/Brand/Candidates/Index.tsx` — reads `flash.success.bulk_result`
 *     and renders the 6s sonner toast with the `View skipped` action.
 */
export interface FlashSuccessObject {
  message?: string
  bulk_result?: BulkResult
}

/**
 * Props for `pages/Brand/Candidates/Show.tsx` (OKS-18 + OKS-21).
 *
 * `cross_brand_signals` is `null` on first paint (Inertia deferred
 * prop); a second roundtrip hydrates it with the aggregate signals.
 *
 * **OKS-21 extension** (locked at TRD v3 publish): `invite` +
 * `invite_actions` extend the shape with the Invites-panel surface.
 * Both ride along on the controller response — no extra fetch.
 * Backend's serialiser is `CandidatesController#serialise_invite` /
 * `#invite_actions_for`. See `app/frontend/types/invites.ts` for
 * the full sub-shapes.
 */
/** OKS-951 — one logged contact attempt, newest-first in the history. */
export interface CandidateContactAttempt {
  id: number
  channel: "email" | "tiktok_dm" | "other"
  /** `null` only if the actor row was lost; renders as a dash. */
  actor_name: string | null
  attempted_at: string
  note: string | null
}

/**
 * OKS-951 — the merged pitch, or the reason there isn't one.
 *
 * THREE states, deliberately not collapsed into one generic error (UX Spec §3):
 * `missing_template` wants a link to Settings, `unknown_field` wants the field
 * NAME so the message can say which one, and a rejected submit is a fourth
 * thing entirely that renders inline under its field. One generic message would
 * serve none of them.
 */
export interface CandidateContactPitch {
  state: "ok" | "missing_template" | "unknown_field"
  /** Non-null iff `state === "ok"`. */
  body: string | null
  /** Non-null iff `state === "unknown_field"` — the offending merge field. */
  field: string | null
}

/** OKS-951 — one channel option and whether it can be used. */
export interface CandidateContactChannel {
  value: "email" | "tiktok_dm" | "other"
  available: boolean
  /**
   * Rendered verbatim under the disabled option — a STRING, not a code, so the
   * copy lives in one tree. Non-null iff `available === false`.
   *
   * ⚠️ The disabled radio is the AFFORDANCE, not the lock:
   * `Candidates::LogContactAttempt` refuses the same case on the POST.
   */
  unavailable_reason: string | null
}

/**
 * OKS-951 — the contact panel prop (UX Spec §2).
 *
 * 🔴 Under CTPO decision D2 the app SENDS NOTHING. This panel renders a pitch
 * to copy; a human sends it from their own mailbox or TikTok and then marks the
 * candidate contacted. Every string here says so, and the primary action is
 * « Marquer comme contacté·e » — never « Envoyer », in any state or tooltip.
 */
export interface CandidateContactPanel {
  channels: CandidateContactChannel[]
  pitch: CandidateContactPitch
  tiktok_profile_url: string
  /** Link to Brand Settings → Candidat·e·s; null when the viewer can't read it. */
  settings_url: string | null
  /** Null when there is no template — nothing to copy, so no primary action. */
  submit_url: string | null
  /** Newest-first. Always ≥1 row once the candidate is `contacted`. */
  attempts: CandidateContactAttempt[]
}

export interface CandidateShowProps {
  candidate: CandidateDetail
  status_history: CandidateStatusEvent[]
  cross_brand_signals: CrossBrandSignals | null
  /**
   * **OKS-628** — TikTok profile oEmbed. `null` on first paint (deferred
   * prop, mirrors `cross_brand_signals`); resolves to `{ html }` after
   * hydrate. See {@link TiktokProfileEmbed}.
   */
  tiktok_profile_embed: TiktokProfileEmbed | null
  actions: CandidateActions
  /**
   * **OKS-948** — where a `revert` would land THIS candidate, so the
   * confirmation dialog can name the destination ("They'll return to X")
   * instead of the pre-OKS-948 fixed "Move back to reviewed", which became
   * a lie once the destination started varying three ways.
   *
   * Server-resolved by `Candidate#revert_destination` — the SAME method
   * `Candidates::Transition` uses to perform the transition, so the copy can
   * never promise a destination the service wouldn't write.
   *
   * INVARIANT: non-null exactly when `actions.revert_url` is non-null.
   *
   * ⚠️ NEVER re-derive this on the client. The `rejected` case reads the
   * audit trail (the status the candidate was rejected FROM), which the
   * client cannot see.
   */
  revert_destination: CandidateStatus | null
  /**
   * **OKS-950 (R4)** — how much of the brand-rejection appeal is left.
   *
   *   `"available"` one appeal remains; the revert dialog REQUIRES a note
   *   `"spent"`     the brand refused twice; `revert_url` is null and the UI
   *                 renders a static line WHERE THE BUTTON WAS
   *   `null`        the appeal doesn't apply (not rejected at the brand gate)
   *
   * ⚠️ This exists so the affordance never just VANISHES. A missing button is
   * indistinguishable from a state-illegal one, and UX Spec §3 refuses a
   * disabled-with-tooltip path — so `"spent"` is a positive signal to render
   * explanatory text in the button's reading position, not the absence of a
   * signal.
   *
   * Server-resolved by `Candidate#brand_appeal_state`, the SAME method
   * `Candidates::EventEligibility` uses to withdraw the event, so the copy
   * and the gate cannot disagree.
   *
   * INVARIANT: `appeal_state === "spent"` ⟺ `actions.revert_url === null`
   * AND `revert_destination === null`. All three move together.
   *
   * ⚠️ NEVER re-derive from `status_history` — the count filters on the
   * rejecting actor's ROLE, which the client cannot see.
   */
  appeal_state: "available" | "spent" | null
  /**
   * Role-only edit gate for the always-editable inline editors
   * (notes / tags / email). True at EVERY status for a write-permitted
   * viewer. Do NOT use this to gate the profile edit — see
   * `can_edit_profile`.
   */
  can_edit: boolean
  /**
   * **OKS-911** — Profile-edit gate for the six create-form fields
   * (tiktok_url / display_name / approx_followers / bio / country / city).
   * Role AND status ∈ {`new`, `reviewed`} (`CandidatePolicy#edit_profile?`).
   * `false` on a DECIDED candidate (`approved` / `rejected` / `invited` /
   * `joined` / `blocklisted`) → the Edit-profile button is DOM-absent
   * (frozen). Sibling to `can_edit` — NOT an overload, because the inline
   * editors must stay editable at every status. Server enforces the same
   * gate independently (a crafted profile PATCH on a decided candidate
   * 404s), so FE gating is convenience, not the security boundary.
   */
  can_edit_profile: boolean
  back_url: string
  /**
   * DISTINCT non-blank tag tokens across the brand, sorted, lowercase.
   * Powers the OKS-18 tags-editor `Command` combobox typeahead (UX
   * Spec §Tags). Reused from `Candidates::FilterOptionsDigest.tags`
   * server-side so the show + index surfaces share a single source
   * of truth.
   */
  brand_tags: string[]
  /**
   * OKS-21 — Active invite summary (`null` when no active invite OR
   * candidate not yet `approved`). Backend defines the 6-value
   * lifecycle enum on `state`; admin view sees the truth (the
   * 3-state collapse is landing-only).
   */
  invite: InviteSummary | null
  /**
   * OKS-21 — Action affordance URLs. Each is `string | null`; `null`
   * means the affordance is not available (state guard or policy).
   * FE renders DOM-absent in both cases (no disabled-with-tooltip
   * path), mirroring the OKS-19 `actions` pattern.
   */
  invite_actions: InviteActions
  /**
   * **OKS-951** — The contact panel, or `null` when it must not render at all
   * (status outside `{approved, contacted}`, a soft-removed candidate, or a
   * viewer who may not log an attempt). Null → DOM-absent, never disabled and
   * never a tooltip — the same hide-when-null rule the action bar uses, so the
   * FE needs no status logic of its own.
   *
   * NOT deferred: the pitch is a server-side string merge, not a fetch.
   */
  contact: CandidateContactPanel | null
  /**
   * **OKS-465** — Removal metadata. `null` while active; `{at, by}`
   * while removed. Drives the removed-state banner (with its Restore
   * button) and the suppression of the pipeline action bar.
   */
  removal: CandidateRemoval | null
  /**
   * **OKS-465** — Remove/restore affordance URLs (separate from the
   * 8-key transition `actions` surface). See {@link CandidateRemovalActions}.
   */
  removal_actions: CandidateRemovalActions
  /**
   * **OKS-949** — whether to render the self-review advisory (UX Spec §2).
   *
   * ONE server-computed signal, true iff the viewer added this candidate AND
   * the candidate sits at one of the three gate statuses AND it is not
   * soft-removed.
   *
   * ⚠️ NEVER re-derive this on the client. `candidate.created_by.id` is on the
   * wire and a client-side comparison would *work*, which is exactly what makes
   * it tempting — but the status band would then be a hand-written TS mirror of
   * a Ruby list, the same drift OKS-948 had to pin with a lock spec.
   *
   * Boolean, never null, never deferred — the banner renders on first paint.
   *
   * 🔴 This gates a SENTENCE and nothing else. It must never gate, disable,
   * hide, or confirm an action: nothing on this page is denied (epic OKS-947
   * D3), so Crew's hide-the-affordance-when-denied convention does not apply.
   */
  self_review_advisory: boolean
}

// ─── OKS-16 — Manual single-add (unchanged) ────────────────────────

export interface CandidatesNewProps {
  brand: Pick<CurrentBrand, "id" | "name" | "slug">
  candidate: CandidateFormState
  currentUser: CurrentUser
}

export interface CandidateFormState {
  tiktok_url: string
  display_name: string
  approx_followers: number | null
  bio: string
  notes: string
  tags: string
  /** OKS-601 — Optional contact email; empty string on a fresh form. */
  email: string
  /** OKS-348 — ISO alpha-2 (defaults to `"FR"` on the Add dialog). */
  country: string
  /** OKS-348 — Freetext city. OKS-713 — optional; empty string when blank. */
  city: string
}

// ─── OKS-16 — Bulk CSV (unchanged) ─────────────────────────────────

export interface CandidatesImportNewProps {
  brand?: Pick<CurrentBrand, "id" | "name" | "slug">
  maxRows: number
  columns: string[]
  result: ImportResult | null
}
